import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search, Ticket, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/explore", label: "Explore" },
  { to: "/map", label: "Map" },
  { to: "/explore?category=Music", label: "Categories" },
  { to: "/replay", label: "Replay" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const doLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/explore?query=${encodeURIComponent(q.trim())}` : "/explore");
    setOpen(false);
  };

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 border-b border-border/70 bg-white/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center gap-4 px-5 sm:px-8">
        <Link to="/" data-testid="navbar-logo" className="flex shrink-0 items-center">
          <Logo size={40} />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "rounded-full px-4 py-2 text-[15px] font-medium transition-colors duration-200",
                  isActive
                    ? "bg-brand/10 text-brand"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden items-center gap-3 lg:flex">
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 rounded-full bg-secondary px-4 py-2.5 transition-shadow focus-within:ring-2 focus-within:ring-brand/30"
          >
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              data-testid="navbar-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search live events, places..."
              className="w-48 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground xl:w-56"
            />
          </form>
          <button
            data-testid="navbar-tickets"
            onClick={() => navigate("/tickets")}
            aria-label="My tickets"
            className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-brand"
          >
            <Ticket className="h-5 w-5" />
          </button>
          {user ? (
            <div className="flex items-center gap-2">
              <button
                data-testid="navbar-user"
                onClick={() => navigate(`/profile/${user.id}`)}
                className="flex items-center gap-2 rounded-full bg-secondary py-1.5 pl-1.5 pr-3 transition-colors hover:bg-secondary/70"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">
                  {initial}
                </span>
                <span className="max-w-[120px] truncate text-sm font-medium text-foreground">
                  {user.name || user.email}
                </span>
              </button>
              <Button
                data-testid="navbar-logout"
                variant="ghost"
                onClick={doLogout}
                aria-label="Log out"
                className="h-10 w-10 rounded-full p-0 text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          ) : (
            <>
              <Button
                data-testid="navbar-login"
                variant="ghost"
                onClick={() => navigate("/login")}
                className="rounded-full font-medium"
              >
                Log in
              </Button>
              <Button
                data-testid="navbar-register"
                onClick={() => navigate("/register")}
                className="rounded-full bg-brand px-5 hover:bg-brand-hover"
              >
                Sign up
              </Button>
            </>
          )}
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-full text-foreground lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="border-t border-border bg-white/95 px-5 py-4 backdrop-blur-xl lg:hidden"
        >
          <form
            onSubmit={submitSearch}
            className="mb-3 flex items-center gap-2 rounded-full bg-secondary px-4 py-3"
          >
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search live events, places..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </form>
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.label}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "rounded-xl px-4 py-3 text-sm font-medium",
                    isActive ? "bg-brand/10 text-brand" : "text-foreground hover:bg-secondary"
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/tickets"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-foreground hover:bg-secondary"
            >
              <Ticket className="h-4 w-4" /> My Tickets
            </NavLink>
            <div className="mt-3 flex gap-2">
              {user ? (
                <Button
                  data-testid="navbar-mobile-logout"
                  variant="outline"
                  onClick={doLogout}
                  className="flex-1 rounded-full"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Log out ({user.name || user.email})
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOpen(false);
                      navigate("/login");
                    }}
                    className="flex-1 rounded-full"
                  >
                    Log in
                  </Button>
                  <Button
                    onClick={() => {
                      setOpen(false);
                      navigate("/register");
                    }}
                    className="flex-1 rounded-full bg-brand hover:bg-brand-hover"
                  >
                    Sign up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Navbar;
